/**
 * ARAdvancedSettings - SLA per type, Request Templates, Required Documents, Webhooks.
 * All in one page with sub-tabs.
 */
import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Button, Paper, Grid, TextField, MenuItem,
  Chip, IconButton, Tooltip, Alert, Snackbar, CircularProgress,
  Switch, FormControlLabel, Table, TableBody, TableCell,
  TableHead, TableRow, Tabs, Tab, Divider,
} from "@mui/material";
import AddIcon    from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon   from "@mui/icons-material/Edit";
import SaveIcon   from "@mui/icons-material/Save";
import api from "../../utils/api";

const cardSx  = { borderRadius:"14px", border:"1px solid rgba(47,99,173,0.08)", boxShadow:"0 4px 16px rgba(0,0,0,0.05)", p:2.5, mb:2.5 };
const fieldSx = { "& .MuiOutlinedInput-root":{ borderRadius:"10px" } };
const btnSx   = { backgroundColor:"#2f63ad", color:"white", fontWeight:700, borderRadius:"10px", textTransform:"none", boxShadow:"none", "&:hover":{ backgroundColor:"#1f4f94", boxShadow:"none" } };

// ── SLA per request type ──────────────────────────────────────────────────────
const SLASettings = () => {
  const [types,  setTypes]  = useState([]);
  const [snack,  setSnack]  = useState({ open:false, msg:"", sev:"success" });
  const [saving, setSaving] = useState({});

  useEffect(() => {
    api.get("/architectural-request-types").then(r => setTypes(r.data || [])).catch(()=>{});
  }, []);

  const handleSave = async (type) => {
    setSaving(p => ({...p, [type.id]: true}));
    try {
      await api.put(`/architectural-request-types/${type.id}/sla`, { sla_days: type.sla_days });
      setSnack({ open:true, msg:`SLA updated for "${type.type}".`, sev:"success" });
    } catch { setSnack({ open:true, msg:"Failed to save SLA.", sev:"error" }); }
    finally { setSaving(p => ({...p, [type.id]: false})); }
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ color:"#6b778c", mb:2 }}>
        Set the target number of days to decision for each request type. Requests exceeding 80% will show a warning; exceeding 100% will show as breached.
      </Typography>
      <Paper elevation={0} sx={{ borderRadius:"12px", border:"1px solid rgba(47,99,173,0.08)", overflow:"hidden" }}>
        <Table size="small">
          <TableHead sx={{ bgcolor:"rgba(47,99,173,0.04)" }}>
            <TableRow>
              {["Request Type","SLA (days)","Action"].map(h => (
                <TableCell key={h} sx={{ fontWeight:700, color:"#223142", fontSize:12 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {types.map(t => (
              <TableRow key={t.id} hover>
                <TableCell sx={{ fontWeight:600, color:"#223142" }}>{t.type}</TableCell>
                <TableCell>
                  <TextField type="number" size="small" value={t.sla_days || ""} placeholder="No SLA"
                    onChange={e => setTypes(prev => prev.map(x => x.id===t.id ? {...x, sla_days: e.target.value ? parseInt(e.target.value,10) : null} : x))}
                    inputProps={{ min:1, max:365 }} sx={{ ...fieldSx, width:120 }} />
                </TableCell>
                <TableCell>
                  <Button size="small" startIcon={saving[t.id] ? <CircularProgress size={12} /> : <SaveIcon fontSize="small" />}
                    onClick={() => handleSave(t)} sx={{ ...btnSx, py:0.5 }}>Save</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={()=>setSnack(s=>({...s,open:false}))} anchorOrigin={{ vertical:"top", horizontal:"center" }}>
        <Alert severity={snack.sev}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

// ── Request Templates ──────────────────────────────────────────────────────────
const TemplatesManager = () => {
  const [templates, setTemplates] = useState([]);
  const [types,     setTypes]     = useState([]);
  const [subtypes,  setSubtypes]  = useState([]);
  const [form,      setForm]      = useState({ name:"", description:"", request_type_id:"", subtype_id:"", default_description:"", default_notes:"" });
  const [editing,   setEditing]   = useState(null);
  const [snack,     setSnack]     = useState({ open:false, msg:"", sev:"success" });

  const load = useCallback(async () => {
    const [tr, ty] = await Promise.all([api.get("/ar-templates"), api.get("/architectural-request-types")]);
    setTemplates(tr.data || []); setTypes(ty.data || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!form.request_type_id) { setSubtypes([]); return; }
    api.get(`/architectural-request-subtypes?typeId=${form.request_type_id}`).then(r => setSubtypes(r.data||[])).catch(()=>{});
  }, [form.request_type_id]);

  const handleSave = async () => {
    if (!form.name) { setSnack({ open:true, msg:"Name required.", sev:"error" }); return; }
    try {
      if (editing) { await api.put(`/ar-templates/${editing}`, form); }
      else         { await api.post("/ar-templates", form); }
      setForm({ name:"", description:"", request_type_id:"", subtype_id:"", default_description:"", default_notes:"" });
      setEditing(null); setSnack({ open:true, msg:"Template saved.", sev:"success" }); load();
    } catch { setSnack({ open:true, msg:"Failed to save.", sev:"error" }); }
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ color:"#6b778c", mb:2 }}>
        Save pre-filled request templates so staff can quickly create common request types.
      </Typography>
      <Paper elevation={0} sx={cardSx}>
        <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:2 }}>
          {editing ? "Edit Template" : "Add Template"}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Template Name *" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} sx={fieldSx} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Description" value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} sx={fieldSx} /></Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth size="small" label="Request Type" value={form.request_type_id} onChange={e=>setForm(f=>({...f,request_type_id:e.target.value,subtype_id:""}))} sx={fieldSx}>
              <MenuItem value="">Any type</MenuItem>
              {types.map(t => <MenuItem key={t.id} value={t.id}>{t.type}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth size="small" label="Subtype" value={form.subtype_id} onChange={e=>setForm(f=>({...f,subtype_id:e.target.value}))} disabled={!subtypes.length} sx={fieldSx}>
              <MenuItem value="">Any subtype</MenuItem>
              {subtypes.map(s => <MenuItem key={s.subtype_id} value={s.subtype_id}>{s.subtype_name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12}><TextField fullWidth multiline rows={2} size="small" label="Default Description" value={form.default_description} onChange={e=>setForm(f=>({...f,default_description:e.target.value}))} sx={fieldSx} /></Grid>
          <Grid item xs={12}><TextField fullWidth multiline rows={2} size="small" label="Default Notes" value={form.default_notes} onChange={e=>setForm(f=>({...f,default_notes:e.target.value}))} sx={fieldSx} /></Grid>
          <Grid item xs={12}>
            <Box sx={{ display:"flex", gap:1 }}>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} sx={btnSx}>
                {editing ? "Update Template" : "Save Template"}
              </Button>
              {editing && <Button variant="outlined" onClick={() => { setEditing(null); setForm({ name:"", description:"", request_type_id:"", subtype_id:"", default_description:"", default_notes:"" }); }} sx={{ borderRadius:"10px", textTransform:"none", fontWeight:600 }}>Cancel</Button>}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ borderRadius:"12px", border:"1px solid rgba(47,99,173,0.08)", overflow:"hidden" }}>
        <Table size="small">
          <TableHead sx={{ bgcolor:"rgba(47,99,173,0.04)" }}>
            <TableRow>{["Name","Type","Subtype",""].map(h=><TableCell key={h} sx={{ fontWeight:700, color:"#223142", fontSize:12 }}>{h}</TableCell>)}</TableRow>
          </TableHead>
          <TableBody>
            {templates.map(t => (
              <TableRow key={t.id} hover>
                <TableCell sx={{ fontWeight:600, color:"#223142" }}>{t.name}{t.description&&<Typography variant="caption" sx={{ display:"block", color:"#6b778c" }}>{t.description}</Typography>}</TableCell>
                <TableCell sx={{ fontSize:12, color:"#374151" }}>{t.type_name||"Any"}</TableCell>
                <TableCell sx={{ fontSize:12, color:"#374151" }}>{t.subtype_name||"Any"}</TableCell>
                <TableCell>
                  <Box sx={{ display:"flex", gap:0.5 }}>
                    <IconButton size="small" onClick={() => { setEditing(t.id); setForm({ name:t.name, description:t.description||"", request_type_id:t.request_type_id||"", subtype_id:t.subtype_id||"", default_description:t.default_description||"", default_notes:t.default_notes||"" }); }}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={async()=>{ await api.delete(`/ar-templates/${t.id}`); load(); }}><DeleteIcon fontSize="small" /></IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {templates.length===0 && <TableRow><TableCell colSpan={4} sx={{ textAlign:"center", py:3, color:"#6b778c" }}>No templates yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Paper>
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={()=>setSnack(s=>({...s,open:false}))} anchorOrigin={{ vertical:"top", horizontal:"center" }}><Alert severity={snack.sev}>{snack.msg}</Alert></Snackbar>
    </Box>
  );
};

// ── Required Documents ─────────────────────────────────────────────────────────
const RequiredDocuments = () => {
  const [docs,    setDocs]    = useState([]);
  const [types,   setTypes]   = useState([]);
  const [form,    setForm]    = useState({ type_id:"", document_name:"", is_required:true, description:"" });
  const [snack,   setSnack]   = useState({ open:false, msg:"", sev:"success" });

  const load = useCallback(async () => {
    const [dr, ty] = await Promise.all([api.get("/ar-required-documents"), api.get("/architectural-request-types")]);
    setDocs(dr.data || []); setTypes(ty.data || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!form.document_name) { setSnack({ open:true, msg:"Document name required.", sev:"error" }); return; }
    try { await api.post("/ar-required-documents", form); setForm({ type_id:"", document_name:"", is_required:true, description:"" }); setSnack({ open:true, msg:"Added.", sev:"success" }); load(); }
    catch { setSnack({ open:true, msg:"Failed to add.", sev:"error" }); }
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ color:"#6b778c", mb:2 }}>
        Define which documents are required for each request type. Members will see this checklist when submitting.
      </Typography>
      <Paper elevation={0} sx={cardSx}>
        <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:2 }}>Add Required Document</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}><TextField select fullWidth size="small" label="Request Type" value={form.type_id} onChange={e=>setForm(f=>({...f,type_id:e.target.value}))} sx={fieldSx}><MenuItem value="">All types</MenuItem>{types.map(t=><MenuItem key={t.id} value={t.id}>{t.type}</MenuItem>)}</TextField></Grid>
          <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Document Name *" value={form.document_name} onChange={e=>setForm(f=>({...f,document_name:e.target.value}))} sx={fieldSx} /></Grid>
          <Grid item xs={12} sm={3}><TextField fullWidth size="small" label="Description" value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} sx={fieldSx} /></Grid>
          <Grid item xs={12} sm={1}><FormControlLabel control={<Switch checked={form.is_required} onChange={e=>setForm(f=>({...f,is_required:e.target.checked}))} size="small" />} label={<Typography variant="caption">Required</Typography>} /></Grid>
          <Grid item xs={12}><Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd} sx={btnSx}>Add</Button></Grid>
        </Grid>
      </Paper>
      <Paper elevation={0} sx={{ borderRadius:"12px", border:"1px solid rgba(47,99,173,0.08)", overflow:"hidden" }}>
        <Table size="small">
          <TableHead sx={{ bgcolor:"rgba(47,99,173,0.04)" }}><TableRow>{["Document","Type","Required","Description",""].map(h=><TableCell key={h} sx={{ fontWeight:700, color:"#223142", fontSize:12 }}>{h}</TableCell>)}</TableRow></TableHead>
          <TableBody>
            {docs.map(d=>(
              <TableRow key={d.id} hover>
                <TableCell sx={{ fontWeight:600, color:"#223142" }}>{d.document_name}</TableCell>
                <TableCell sx={{ fontSize:12, color:"#374151" }}>{types.find(t=>t.id===d.type_id)?.type||"All types"}</TableCell>
                <TableCell><Chip size="small" label={d.is_required?"Required":"Optional"} sx={{ fontWeight:700, fontSize:11, borderRadius:"8px", bgcolor:d.is_required?"rgba(220,38,38,0.1)":"rgba(0,0,0,0.06)", color:d.is_required?"#dc2626":"#6b778c" }} /></TableCell>
                <TableCell sx={{ fontSize:12, color:"#6b778c" }}>{d.description||"N/A"}</TableCell>
                <TableCell><IconButton size="small" color="error" onClick={async()=>{ await api.delete(`/ar-required-documents/${d.id}`); load(); }}><DeleteIcon fontSize="small" /></IconButton></TableCell>
              </TableRow>
            ))}
            {docs.length===0&&<TableRow><TableCell colSpan={5} sx={{ textAlign:"center", py:3, color:"#6b778c" }}>No required documents configured.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Paper>
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={()=>setSnack(s=>({...s,open:false}))} anchorOrigin={{ vertical:"top", horizontal:"center" }}><Alert severity={snack.sev}>{snack.msg}</Alert></Snackbar>
    </Box>
  );
};

// ── Webhooks ───────────────────────────────────────────────────────────────────
const WebhookSettings = () => {
  const [hooks,  setHooks]  = useState([]);
  const [form,   setForm]   = useState({ url:"", events:"status_change,comment_added,created", secret_key:"" });
  const [snack,  setSnack]  = useState({ open:false, msg:"", sev:"success" });

  const load = useCallback(async () => { const r = await api.get("/ar-webhooks"); setHooks(r.data||[]); }, []);
  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!form.url) { setSnack({ open:true, msg:"URL required.", sev:"error" }); return; }
    try { await api.post("/ar-webhooks", form); setForm({ url:"", events:"status_change,comment_added,created", secret_key:"" }); setSnack({ open:true, msg:"Webhook added.", sev:"success" }); load(); }
    catch { setSnack({ open:true, msg:"Failed to add.", sev:"error" }); }
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ color:"#6b778c", mb:2 }}>
        Send HTTP POST notifications to external systems when AR events occur (status changes, comments, creation).
      </Typography>
      <Alert severity="info" sx={{ mb:2, borderRadius:2 }}>
        Your endpoint must respond with HTTP 2xx within 10 seconds. Failed deliveries are logged but not retried.
      </Alert>
      <Paper elevation={0} sx={cardSx}>
        <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:2 }}>Add Webhook</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Endpoint URL *" placeholder="https://your-system.com/webhook" value={form.url} onChange={e=>setForm(f=>({...f,url:e.target.value}))} sx={fieldSx} /></Grid>
          <Grid item xs={12} sm={3}><TextField fullWidth size="small" label="Events (comma-separated)" value={form.events} onChange={e=>setForm(f=>({...f,events:e.target.value}))} sx={fieldSx} helperText="status_change, comment_added, created, *" /></Grid>
          <Grid item xs={12} sm={3}><TextField fullWidth size="small" label="Secret Key (optional)" value={form.secret_key} onChange={e=>setForm(f=>({...f,secret_key:e.target.value}))} sx={fieldSx} helperText="Sent as X-AR-Webhook-Secret header" /></Grid>
          <Grid item xs={12}><Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd} sx={btnSx}>Add Webhook</Button></Grid>
        </Grid>
      </Paper>
      <Paper elevation={0} sx={{ borderRadius:"12px", border:"1px solid rgba(47,99,173,0.08)", overflow:"hidden" }}>
        <Table size="small">
          <TableHead sx={{ bgcolor:"rgba(47,99,173,0.04)" }}><TableRow>{["URL","Events","Status",""].map(h=><TableCell key={h} sx={{ fontWeight:700, color:"#223142", fontSize:12 }}>{h}</TableCell>)}</TableRow></TableHead>
          <TableBody>
            {hooks.map(h=>(
              <TableRow key={h.id} hover>
                <TableCell sx={{ fontSize:12, color:"#223142", maxWidth:280 }}><Typography variant="caption" noWrap sx={{ display:"block", maxWidth:260 }}>{h.url}</Typography></TableCell>
                <TableCell sx={{ fontSize:11, color:"#6b778c" }}>{h.events}</TableCell>
                <TableCell><Chip size="small" label={h.is_active?"Active":"Inactive"} sx={{ fontWeight:700, fontSize:11, borderRadius:"8px", bgcolor:h.is_active?"rgba(34,197,94,0.1)":"rgba(0,0,0,0.06)", color:h.is_active?"#16a34a":"#6b778c" }} onClick={async()=>{ await api.patch(`/ar-webhooks/${h.id}`,{is_active:!h.is_active}); load(); }} /></TableCell>
                <TableCell><IconButton size="small" color="error" onClick={async()=>{ await api.delete(`/ar-webhooks/${h.id}`); load(); }}><DeleteIcon fontSize="small" /></IconButton></TableCell>
              </TableRow>
            ))}
            {hooks.length===0&&<TableRow><TableCell colSpan={4} sx={{ textAlign:"center", py:3, color:"#6b778c" }}>No webhooks configured.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Paper>
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={()=>setSnack(s=>({...s,open:false}))} anchorOrigin={{ vertical:"top", horizontal:"center" }}><Alert severity={snack.sev}>{snack.msg}</Alert></Snackbar>
    </Box>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────────
const ARAdvancedSettings = () => {
  const [tab, setTab] = useState(0);
  return (
    <Box>
      <Box sx={{ mb:3 }}>
        <Typography variant="h6" sx={{ fontWeight:700, color:"#223142" }}>Advanced Settings</Typography>
        <Typography variant="body2" sx={{ color:"#6b778c" }}>SLA targets, request templates, required documents, and webhook integrations.</Typography>
      </Box>
      <Paper elevation={0} sx={{ borderRadius:"14px", border:"1px solid rgba(47,99,173,0.08)", mb:3, overflow:"hidden" }}>
        <Tabs value={tab} onChange={(_,v)=>setTab(v)} sx={{ px:1, "& .MuiTab-root":{ textTransform:"none", fontWeight:600, minHeight:44, fontSize:13 } }}>
          <Tab label="SLA Targets" />
          <Tab label="Request Templates" />
          <Tab label="Required Documents" />
          <Tab label="Webhooks" />
        </Tabs>
      </Paper>
      {tab===0 && <SLASettings />}
      {tab===1 && <TemplatesManager />}
      {tab===2 && <RequiredDocuments />}
      {tab===3 && <WebhookSettings />}
    </Box>
  );
};

export default ARAdvancedSettings;
